import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import HiveFormDialog from "@/components/HiveFormDialog";
import PolicyAcceptanceDialog from "@/components/PolicyAcceptanceDialog";
import ShareLinkDialog from "@/components/ShareLinkDialog";
import { climateZoneForLat, zoneStyle } from "@/lib/climate";
import { formatTemp, tempUnitSuffix } from "@/lib/format";
import TempSparkline from "@/components/TempSparkline";
import { toast } from "sonner";
import { Plus, MapPin, Crown, Download } from "lucide-react";

const STRENGTH_COLOR = {
  weak: "bg-red-50 text-red-700 border-red-200",
  moderate: "bg-amber-50 text-amber-800 border-amber-200",
  strong: "bg-emerald-50 text-emerald-800 border-emerald-200",
  thriving: "bg-green-100 text-green-900 border-green-300",
};

export default function Hives() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hives, setHives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [zoneFilter, setZoneFilter] = useState("all"); // "all" | climate zone key
  const [todayByHive, setTodayByHive] = useState({});   // hive_id → forecast slot
  const [inspByHive, setInspByHive] = useState({});     // hive_id → {series, months}
  const [harvByHive, setHarvByHive] = useState({});     // hive_id → {series, months}
  // Sparkline mode: initial value from localStorage for instant render, then
  // we fetch the server-side preference (the authoritative copy across devices)
  // and sync if it differs. Subsequent changes are PATCHed back to the server.
  const [sparkMode, setSparkMode] = useState(() => localStorage.getItem("hives.sparkMode") || "forecast");
  const sparkModeSyncedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/preferences").then((r) => {
      if (cancelled) return;
      const remote = r.data?.sparkline_mode;
      if (remote && ["forecast", "inspections", "harvest"].includes(remote)) {
        setSparkMode(remote);
      }
      sparkModeSyncedRef.current = true;
    }).catch(() => { sparkModeSyncedRef.current = true; });
    return () => { cancelled = true; };
  }, []);

  // Persist to server (debounced via setTimeout) + localStorage. We only PATCH
  // after the initial GET completed so we don't immediately overwrite the
  // remote value with the localStorage default. "forecast" is the implicit
  // default — selecting it CLEARS any stored override so the user always
  // returns to the default on later visits.
  useEffect(() => {
    localStorage.setItem("hives.sparkMode", sparkMode);
    if (!sparkModeSyncedRef.current) return;
    const t = setTimeout(() => {
      const body = sparkMode === "forecast"
        ? { sparkline_mode_clear: true }
        : { sparkline_mode: sparkMode };
      api.patch("/auth/preferences", body).catch(() => { /* silent */ });
    }, 300);
    return () => clearTimeout(t);
  }, [sparkMode]);

  const load = () => {
    setLoading(true);
    api.get("/hives").then((r) => setHives(r.data)).finally(() => setLoading(false));
    // Today's forecast — best-effort; silently no-ops for free users or no-key envs
    api.get("/weather/today")
      .then((r) => {
        const map = {};
        for (const t of r.data || []) map[t.hive_id] = t;
        setTodayByHive(map);
      })
      .catch(() => { /* silent */ });
    // 12-mo historical inspection trend — silent on failure (Pro-gated)
    api.get("/trends/sparklines", { params: { metric: "inspections" } })
      .then((r) => {
        const map = {};
        for (const t of r.data || []) map[t.hive_id] = t;
        setInspByHive(map);
      })
      .catch(() => { /* silent */ });
    api.get("/trends/sparklines", { params: { metric: "harvests_lb" } })
      .then((r) => {
        const map = {};
        for (const t of r.data || []) map[t.hive_id] = t;
        setHarvByHive(map);
      })
      .catch(() => { /* silent */ });
  };

  useEffect(load, []);

  // Compute zone counts for the quick filter — only show filter if 2+ zones exist
  const zoneCounts = useMemo(() => {
    const m = new Map();
    for (const h of hives) {
      const z = climateZoneForLat(h.latitude);
      if (!z) continue;
      m.set(z, (m.get(z) || 0) + 1);
    }
    return m;
  }, [hives]);

  // If the currently-selected sparkline mode has no data (e.g. user picked
  // "harvest" but later removed all harvests), fall back to "forecast" — the
  // implicit default that's always selectable, even if the user has no
  // location pinned yet (the cards just won't show a sparkline in that case).
  useEffect(() => {
    if (sparkMode === "forecast") return;
    const has = {
      inspections: Object.keys(inspByHive).length > 0,
      harvest: Object.keys(harvByHive).length > 0,
    };
    if (!has[sparkMode]) setSparkMode("forecast");
  }, [inspByHive, harvByHive, sparkMode]);

  const filteredHives = useMemo(() => {
    if (zoneFilter === "all") return hives;
    return hives.filter((h) => climateZoneForLat(h.latitude) === zoneFilter);
  }, [hives, zoneFilter]);

  const handleNewHiveClick = () => {
    // If user has not yet accepted the site policy, intercept and show
    // the policy acceptance dialog first (especially before their first hive).
    if (user && !user.policy_accepted_at) {
      setPolicyOpen(true);
      return;
    }
    setOpen(true);
  };

  const handlePolicyAccepted = () => {
    // After acceptance, open the actual hive creation dialog.
    setOpen(true);
  };

  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const downloadApiary = async (fmt) => {
    setExporting(true);
    setExportMenuOpen(false);
    try {
      const r = await api.get(`/exports/apiary.${fmt}`, { responseType: "blob" });
      const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `apiary-summary-${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Apiary summary downloaded (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(e.response?.status === 402
        ? "Apiary export is a Pro feature."
        : e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="hives-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Your apiary</div>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Hives</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Button
              variant="outline"
              className="rounded-full border-stone-300 text-stone-700"
              data-testid="export-apiary-button"
              onClick={() => setExportMenuOpen((o) => !o)}
              disabled={exporting || hives.length === 0}
              title="Download apiary summary (CSV or PDF)"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Exporting…" : "Export"}
            </Button>
            {exportMenuOpen && (
              <>
                <button
                  className="fixed inset-0 z-10 cursor-default"
                  aria-hidden
                  onClick={() => setExportMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 w-56 bg-white border border-stone-200 rounded-xl shadow-lg p-1" data-testid="export-menu">
                  <button
                    onClick={() => downloadApiary("pdf")}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-amber-50 flex flex-col"
                    data-testid="export-pdf-btn"
                  >
                    <span className="font-medium text-stone-900">📄 PDF cover sheet</span>
                    <span className="text-xs text-stone-500">One-page summary, print-ready</span>
                  </button>
                  <button
                    onClick={() => downloadApiary("csv")}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-amber-50 flex flex-col"
                    data-testid="export-csv-btn"
                  >
                    <span className="font-medium text-stone-900">📊 CSV spreadsheet</span>
                    <span className="text-xs text-stone-500">Per-hive metrics + climate zones</span>
                  </button>
                </div>
              </>
            )}
          </div>
          <ShareLinkDialog scope="apiary" label="Share apiary" />
          <Button
            className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
            data-testid="add-hive-button"
            onClick={handleNewHiveClick}
          >
            <Plus className="w-4 h-4 mr-2" /> New hive
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <HiveFormDialog onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      <PolicyAcceptanceDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        onAccepted={handlePolicyAccepted}
      />

      {!loading && hives.length > 0 && zoneCounts.size >= 2 && (
        <div className="flex flex-wrap gap-2" data-testid="hives-zone-filter">
          <button
            onClick={() => setZoneFilter("all")}
            className={`text-xs px-3 py-1 rounded-full border transition ${
              zoneFilter === "all"
                ? "bg-stone-900 border-stone-900 text-white"
                : "border-stone-300 hover:border-stone-400 text-stone-700"
            }`}
            data-testid="zone-filter-all"
          >
            All zones <span className="opacity-70">({hives.length})</span>
          </button>
          {[...zoneCounts.entries()].map(([z, n]) => {
            const s = zoneStyle(z);
            if (!s) return null;
            const active = zoneFilter === z;
            return (
              <button
                key={z}
                onClick={() => setZoneFilter(z)}
                className={`text-xs px-3 py-1 rounded-full border transition inline-flex items-center gap-1.5 ${
                  active
                    ? "bg-stone-900 border-stone-900 text-white"
                    : `${s.chip} hover:opacity-80`
                }`}
                data-testid={`zone-filter-${z}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-white/80" : s.dot}`} aria-hidden />
                {s.label} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="text-stone-500">Loading…</div>
      ) : hives.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-3xl p-16 text-center">
          <div className="font-display text-2xl font-medium">No hives yet</div>
          <p className="text-stone-500 mt-2">Add your first colony to start journaling.</p>
        </div>
      ) : filteredHives.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-stone-300 rounded-2xl">
          <p className="text-sm text-stone-500">No hives in this zone. Try another filter.</p>
        </div>
      ) : (
        <HiveGroups
          hives={filteredHives}
          todayByHive={todayByHive}
          inspByHive={inspByHive}
          harvByHive={harvByHive}
          sparkMode={sparkMode}
          setSparkMode={setSparkMode}
          navigate={navigate}
        />
      )}
    </div>
  );
}

function HiveGroups({ hives, todayByHive = {}, inspByHive = {}, harvByHive = {}, sparkMode = "forecast", setSparkMode, navigate }) {
  const groups = hives.reduce((acc, h) => {
    const k = (h.yard || "").trim() || "Ungrouped";
    (acc[k] = acc[k] || []).push(h);
    return acc;
  }, {});
  const yardKeys = Object.keys(groups).sort((a, b) => (a === "Ungrouped" ? 1 : b === "Ungrouped" ? -1 : a.localeCompare(b)));
  const showHeaders = yardKeys.length > 1 || (yardKeys.length === 1 && yardKeys[0] !== "Ungrouped");
  const hasInsp = Object.keys(inspByHive).length > 0;
  const hasHarv = Object.keys(harvByHive).length > 0;
  const hasForecast = Object.keys(todayByHive).length > 0;

  const modes = [
    // Forecast is the canonical default — never disabled. If the user has
    // no coords pinned, the cards just won't render a sparkline; that's
    // expected and the tooltip nudges them to add a location.
    { key: "forecast", label: "5-day forecast", enabled: true, hint: hasForecast ? "5-day forecast highs" : "Pin a location to a hive to see forecast trends" },
    { key: "inspections", label: "12-mo inspections", enabled: hasInsp, hint: !hasInsp ? "Pro feature — log inspections to see history" : "Last 12 months of inspections" },
    { key: "harvest", label: "12-mo harvest", enabled: hasHarv, hint: !hasHarv ? "Pro feature — log harvests to see totals" : "Last 12 months of harvest (lb)" },
  ];

  return (
    <div className="space-y-10" data-testid="hives-grouped">
      {(hasInsp || hasForecast || hasHarv) && setSparkMode && (
        <div className="flex justify-end" data-testid="sparkline-mode-toggle">
          <div className="inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-full p-0.5 text-xs">
            <span className="px-2 text-stone-500 uppercase tracking-wider text-[10px]">Sparkline</span>
            {modes.map((m) => {
              const isActive = sparkMode === m.key && m.enabled;
              return (
                <button
                  key={m.key}
                  onClick={() => setSparkMode(m.key)}
                  className={`px-2.5 py-1 rounded-full transition ${
                    isActive
                      ? "bg-white text-stone-900 shadow-sm font-medium"
                      : m.enabled
                        ? "text-stone-500 hover:text-stone-700"
                        : "text-stone-400 opacity-60 cursor-not-allowed"
                  }`}
                  data-testid={`spark-mode-${m.key}`}
                  disabled={!m.enabled}
                  title={m.hint}
                  aria-pressed={isActive}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {yardKeys.map((y) => (
        <div key={y}>
          {showHeaders && (
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="font-display text-xl font-medium">{y}</h2>
              <span className="text-sm text-stone-500">({groups[y].length})</span>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`hives-grid-${y.replace(/\s+/g, "-").toLowerCase()}`}>
            {groups[y].map((h, i) => (
              <HiveCard
                key={h.id}
                h={h}
                i={i}
                today={todayByHive[h.id]}
                insp={inspByHive[h.id]}
                harv={harvByHive[h.id]}
                sparkMode={sparkMode}
                navigate={navigate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const RATING_STYLE = {
  great: "bg-emerald-50 text-emerald-800 border-emerald-200",
  ok:    "bg-amber-50 text-amber-800 border-amber-200",
  avoid: "bg-rose-50 text-rose-800 border-rose-200",
};

function HiveCard({ h, i, today, insp, harv, sparkMode = "forecast", navigate }) {
  const { user: prefs } = useAuth();
  const zone = climateZoneForLat(h.latitude);
  const zStyle = zone ? zoneStyle(zone) : null;
  const toUnit = (f) => (prefs?.temperature_unit === "C" ? Math.round((f - 32) * 5 / 9) : Math.round(f));

  // Build sparkline data based on the active mode. Each mode also produces an
  // `onClick(idx)` handler that deep-links into a relevant HiveDetail tab.
  const spark = useMemo(() => {
    const monthLabel = (ym) => {
      const [y, m] = ym.split("-");
      const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(m) - 1] || "";
      return `${M} ${y.slice(2)}`;
    };

    if (sparkMode === "inspections" && insp?.series?.length) {
      const labels = (insp.months || []).map(monthLabel);
      return {
        values: insp.series,
        labels,
        colors: insp.series.map((v) => (v > 0 ? "#10b981" : "#a8a29e")),
        unit: "inspections",
        width: 110,
        suffix: "12mo",
        onPointClick: (idx) => {
          const ym = insp.months[idx];
          navigate?.(`/app/hives/${h.id}?tab=trends${ym ? `&month=${ym}` : ""}`);
        },
      };
    }
    if (sparkMode === "harvest" && harv?.series?.length) {
      const labels = (harv.months || []).map(monthLabel);
      return {
        values: harv.series,
        labels,
        colors: harv.series.map((v) => (v > 0 ? "#f59e0b" : "#a8a29e")),
        unit: "lb",
        width: 110,
        suffix: "12mo",
        onPointClick: (idx) => {
          const ym = harv.months[idx];
          navigate?.(`/app/hives/${h.id}?tab=trends${ym ? `&month=${ym}` : ""}`);
        },
      };
    }
    if (sparkMode === "forecast" && today?.temp_series_f?.length) {
      const labels = ["Today", "Tomorrow", "Day 3", "Day 4", "Day 5"].slice(0, today.temp_series_f.length);
      const colors = (today.rating_series || []).map((r) => (
        r === "great" ? "#10b981" : r === "ok" ? "#f59e0b" : r === "avoid" ? "#f43f5e" : "#a8a29e"
      ));
      return {
        values: today.temp_series_f.map(toUnit),
        labels,
        colors,
        unit: tempUnitSuffix(prefs),
        width: 70,
        suffix: "5d",
        onPointClick: () => navigate?.(`/app/hives/${h.id}?tab=weather`),
      };
    }
    return null;
  }, [sparkMode, insp, harv, today, navigate, h.id, prefs?.temperature_unit]);  // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Link
      to={`/app/hives/${h.id}`}
      className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-sm hover:-translate-y-0.5 transition-all duration-300 fade-up relative"
      style={{ animationDelay: `${i * 50}ms` }}
      data-testid={`hive-card-${h.id}`}
    >
      <span
        className="absolute left-0 top-0 bottom-0 w-1.5"
        style={{ background: h.color || "#EAB308" }}
        aria-hidden
      />
      <div className="aspect-[4/3] bg-stone-100 overflow-hidden relative">
        {h.cover_image ? (
          <img src={h.cover_image} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-yellow-100 to-amber-50">
            <div className="text-6xl font-display font-light text-amber-700/60">{h.name?.[0]?.toUpperCase()}</div>
          </div>
        )}
        {today && (
          <div
            className={`absolute top-3 right-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${RATING_STYLE[today.rating] || RATING_STYLE.ok} backdrop-blur-sm bg-opacity-90 shadow-sm`}
            title={`${today.summary || today.rating_label} · ${today.rating_label} for inspecting`}
            data-testid={`hive-card-forecast-${h.id}`}
          >
            {today.icon && (
              <img src={`https://openweathermap.org/img/wn/${today.icon}.png`} alt="" className="w-4 h-4 -ml-1" />
            )}
            <span className="font-medium tabular-nums">{formatTemp(today.temp_max_f, prefs)}</span>
            <span className="text-stone-500">·</span>
            <span className="capitalize">{today.rating_label}</span>
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display text-xl font-medium truncate">{h.name}</div>
            <div className="flex items-center gap-1 text-xs text-stone-500 mt-1" title={h.weather_location_label || h.location || ""}>
              <MapPin className={`w-3 h-3 shrink-0 ${h.latitude != null ? "text-emerald-600" : "text-stone-400"}`} />
              <span className="truncate">
                {h.weather_location_label || h.location || "No location"}
              </span>
              {h.latitude != null && (
                <span className="text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-1.5 py-0.5 ml-1 shrink-0">
                  pinned
                </span>
              )}
            </div>
            {zStyle && (
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${zStyle.chip}`}
                  title={`Climate zone — drives reminders & forecast`}
                  data-testid={`hive-card-zone-${h.id}`}
                >
                  <span className={`w-1 h-1 rounded-full ${zStyle.dot}`} aria-hidden />
                  {zStyle.label}
                </span>
                {spark && (
                  <span
                    className="inline-flex items-center gap-1"
                    title={
                      sparkMode === "forecast" ? "Tap a day to open the Forecast tab" :
                      sparkMode === "harvest"  ? "Tap a month to open Trends" :
                      "Tap a month to open Trends"
                    }
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  >
                    <TempSparkline
                      values={spark.values}
                      labels={spark.labels}
                      colors={spark.colors}
                      unit={spark.unit}
                      width={spark.width}
                      onPointClick={spark.onPointClick}
                    />
                    <span className="text-[10px] text-stone-400 tabular-nums">
                      {spark.suffix}
                    </span>
                  </span>
                )}
              </div>
            )}
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${STRENGTH_COLOR[h.strength] || ""}`}>
            {h.strength}
          </span>
        </div>
        {h.species && (
          <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-3 truncate" title={h.species}>
            <span className="text-[10px] uppercase tracking-wider text-stone-400 shrink-0">Species</span>
            <span className="truncate">{h.species}</span>
          </div>
        )}
        {h.queen_color && (
          <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-2">
            <Crown className="w-3 h-3" /> Queen · {h.queen_color}{h.queen_year ? ` · ${h.queen_year}` : ""}
          </div>
        )}
        {h.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {h.tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wide bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">{t}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

// HiveFormDialog moved to /components/HiveFormDialog.jsx
